import {
  userSchema,
  taxpayerSchema,
  // taxpayerGroupRulesSchema,
  unitCommonAddressSchema,
  getUnitCoreSchema,
} from "./ValidationSchemas";
import { validateEgyptianPhoneNumberWithPhoneInput } from "../../../helpers/Validation";
import { isValidPhoneNumber } from "libphonenumber-js";

// ---------- helpers ----------
export const get = (obj, path) =>
  path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);

const getAttachmentErrorMessage = (key) => {
  switch (key) {
    case "commercial_register_attachment":
      return "يجب رفع صورة السجل التجاري";

    case "tax_card_attachment":
      return "يجب رفع صورة البطاقة الضريبية";

    case "national_id_attachment":
      return "يجب رفع مرفق الرقم القومي";

    case "passport_attachment":
      return "يجب رفع مرفق جواز السفر";

    case "other_attachment":
      return "يجب رفع المرفق";

    default:
      return "يجب رفع الصورة";
  }
};
const defaultMessageFor = (type, name, key) => {
  if (type === "select") return `يجب اختيار ${name}`;
  if (type === "input") return `يجب إدخال ${name}`;
  if (type === "array") return `يجب اختيار ${name}`;
  if (type === "file") return `يجب رفع الصورة`;
  if (type === "customFile") return getAttachmentErrorMessage(key);
  return `الحقل ${name} غير صالح`;
};

const defaultValidateRequired = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

/**
 * Generic per-field validator that works with any schema.
 * Each schema item supports:
 *  - key: error key
 *  - validKey?: key for setValidFields (defaults to key)
 *  - path: dot path to data (e.g., 'unit.unit_id' or 'taxpayer.national_id_attachment.full_url')
 *  - type: 'input'|'select'|'array'|'file'
 *  - name: Arabic display name for messages
 *  - required?: boolean (default true); for optional fields set required:false
 *  - maxLength?: number (strings)
 *  - allowedPattern?: RegExp (strings)
 *  - message?: string (override default message)
 *  - patternMessage?: string
 *  - maxLengthMessage?: string
 *  - when?: (data) => boolean (only validate if true)
 *  - customValidate?: (value, data) => boolean | { valid:boolean; message?:string }
 *
 * @returns boolean
 */
export function validateForm({
  schema,
  data,
  setValidFields,
  setError,
  validateRequired = defaultValidateRequired,
  messageFor = defaultMessageFor,
}) {
  let isFormValid = true;

  for (const field of schema) {
    const {
      key,
      validKey,
      path,
      type,
      name,
      required,
      maxLength,
      allowedPattern,
      when,
      message,
      patternMessage,
      maxLengthMessage,
      customValidate,
    } = field;

    if (typeof when === "function" && !when(data)) {
      // Only skip showing error, but DO NOT mark as valid
      continue;
    }

    const value = get(data, path);
    const isRequired = typeof required === "boolean" ? required : true;
    let valid = true;
    let finalMessage;

    // (1) Required check
    if (isRequired) {
      valid = validateRequired(value);
      if (type === "array") {
        valid = valid && Array.isArray(value) && value.length > 0;
      }
    }

    // (2) maxLength (string)
    if (valid && maxLength && typeof value === "string") {
      if (value.trim().length > maxLength) {
        valid = false;
        finalMessage =
          maxLengthMessage || `الحد الأقصى لطول ${name} هو ${maxLength} حرفًا`;
      }
    }

    // (3) allowedPattern (string)
    if (
      valid &&
      allowedPattern instanceof RegExp &&
      typeof value === "string" &&
      value.trim() !== ""
    ) {
      if (!allowedPattern.test(value)) {
        valid = false;
        finalMessage = patternMessage || `صيغة ${name} غير صحيحة`;
      }
    }

    // (4) customValidate
    if (valid && typeof customValidate === "function") {
      const result = customValidate(value, data);
      if (typeof result === "boolean") {
        valid = result;
      } else if (result && typeof result === "object") {
        valid = !!result.valid;
        if (!valid && result.message) finalMessage = result.message;
      }
    }

    // (5) Write result
    if (!valid) {
      setValidFields((prev) => ({ ...prev, [validKey || key]: false }));
      setError(key, finalMessage || message || messageFor(type, name, key));
      isFormValid = false;
    } else {
      setError(key, "");
      setValidFields((prev) => ({ ...prev, [validKey || key]: true }));
    }
  }

  return isFormValid;
}

/**
 * Group-level rules (e.g., "at least one of")
 * Rule shape:
 *  {
 *    name: 'اسم المجموعة', // optional
 *    type: 'atLeastOne',
 *    when?: (data) => boolean,
 *    paths: ['taxpayer.commercial_register', 'taxpayer.tax_card_number', ...],
 *    // Keys that should show errors if the rule fails:
 *    errorKeys: ['commercial_register', 'tax_card_number', 'other_attachment_name'],
 *    message: 'يجب إدخال حقل واحد على الأقل من هذه الحقول',
 *    onFail?: (data, helpers) => void, // e.g., setUnitOwnerTypeErrorMessage(...)
 *  }
 *
 * Returns boolean
 */
export function runGroupRules({
  rules,
  data,
  setValidFields,
  setError,
  validateRequired = defaultValidateRequired,
}) {
  let allValid = true;

  for (const rule of rules) {
    if (typeof rule.when === "function" && !rule.when(data)) {
      // Clear group-scope error keys
      if (Array.isArray(rule.errorKeys)) {
        for (const k of rule.errorKeys) {
          setError(k, "");
          setValidFields((prev) => ({ ...prev, [k]: true }));
        }
      }
      continue;
    }

    // handle different rule types
    if (rule.type === "atLeastOne") {
      const values = (rule.paths || []).map((p) => get(data, p));
      const ok = values.some((v) => validateRequired(v));
      if (!ok) {
        allValid = false;
        if (Array.isArray(rule.errorKeys)) {
          for (const k of rule.errorKeys) {
            setError(k, rule.message);
            setValidFields((prev) => ({ ...prev, [k]: false }));
          }
        }
        if (typeof rule.onFail === "function") {
          rule.onFail(data, { setError, setValidFields });
        }
      } else {
        if (Array.isArray(rule.errorKeys)) {
          for (const k of rule.errorKeys) {
            setError(k, "");
            setValidFields((prev) => ({ ...prev, [k]: true }));
          }
        }
      }
    }
  }

  return allValid;
}

export const validateBeforeSubmit = (
  declarationRequest,
  validateRequired,
  setValidFields,
  setError,
  validateNationalIDWithoutAgeLimit,
  setUnitOwnerTypeErrorMessage,
  validateOnlyUserAndTaxPayer,
) => {
  // Inject external helpers into data so custom validators/group rules can use them
  const data = {
    ...declarationRequest,
    __validateNationalID: validateNationalIDWithoutAgeLimit,
    __setUnitOwnerTypeErrorMessage: setUnitOwnerTypeErrorMessage,
  };
  let isFormValid;
  // 1) User
  const isUserValid = validateForm({
    schema: userSchema,
    data,
    setValidFields,
    setError,
    validateRequired,
  });

  // 2) Taxpayer fields
  const isTaxPayerFieldsValid = validateForm({
    schema: taxpayerSchema,
    data,
    setValidFields,
    setError,
    validateRequired,
  });

  // 2-b) Taxpayer group rules (corporate "at least one")
  // Clear top message when group passes
  if (typeof setUnitOwnerTypeErrorMessage === "function") {
    setUnitOwnerTypeErrorMessage("");
  }
  // const isTaxPayerGroupsValid = runGroupRules({
  //   rules: taxpayerGroupRulesSchema,
  //   data,
  //   setValidFields,
  //   setError,
  //   validateRequired,
  // });

  const isTaxPayerValid = isTaxPayerFieldsValid;

  isFormValid = isUserValid && isTaxPayerValid;

  if (!validateOnlyUserAndTaxPayer) {
    // 3) Unit Common Address
    const isUnitCommonAddressValid = validateForm({
      schema: unitCommonAddressSchema,
      data,
      setValidFields,
      setError,
      validateRequired,
    });

    // 4) Unit core per property_type_id
    let isUnitCorePerTypeValid = true;
    const unitCoreSchema = getUnitCoreSchema(data);
    if (unitCoreSchema) {
      isUnitCorePerTypeValid = validateForm({
        schema: unitCoreSchema,
        data,
        setValidFields,
        setError,
        validateRequired,
      });
    }

    // Final result
    isFormValid =
      isUserValid &&
      isTaxPayerValid &&
      isUnitCommonAddressValid &&
      isUnitCorePerTypeValid;
  }

  return isFormValid;
};

export const validatePhoneBeforeSubmit = (
  value,
  dialCode,
  countryCode,
  setPhoneError,
  type_id,
  applicantRoleId,
  national_id,
) => {
  // Phone is only required/validated for applicant roles 5, 6 and 7
  if (![5, 6, 7].includes(parseInt(applicantRoleId))) {
    setPhoneError(null);
    return true;
  }

  const nationalNumber = value?.replace(dialCode, "")?.replace(/\D/g, "");

  if (
    parseInt(type_id) === 1 &&
    (value === dialCode ||
      value === `+${dialCode}` ||
      !value ||
      value === undefined)
  ) {
    setPhoneError("يجب ادخال رقم الهاتف المحمول");
    return false;
  }

  if (
    parseInt(national_id) === 63 &&
    parseInt(type_id) === 1 &&
    !validateEgyptianPhoneNumberWithPhoneInput(value)
  ) {
    setPhoneError(
      "رقم الهاتف المدخل يجب أن يكون رقم هاتف محمول مصريًا صالحًا. يُرجى اختيار مفتاح الدولة (+20) وإدخال رقم هاتف مصري صحيح",
    );
    return false;
  }

  // For non-type 1, allow dialCode-only
  if (parseInt(type_id) !== 1 && value === dialCode) {
    setPhoneError(null);
    return true;
  }

  // Egypt-specific rules (fallback if not using national_id)
  if (countryCode === "eg" && value != "20" && value != "+20") {
    const regex = /^(?:0?)(10|11|12|15)\d{8}$/;
    if (!regex.test(nationalNumber)) {
      setPhoneError(
        "رقم الهاتف المدخل يجب أن يكون رقم هاتف محمول مصريًا صالحًا. يُرجى اختيار مفتاح الدولة (+20) وإدخال رقم هاتف مصري صحيح",
      );
      return false;
    }
  }

  // General validation for other countries
  if (!isValidPhoneNumber("+" + value)) {
    setPhoneError(
      "يجب ادخال الصيغة الصحيحة لرقم الهاتف المحمول وفقا للدولة المختارة",
    );
    return false;
  }

  // Length rule (≤15 digits excluding country code)
  if (nationalNumber?.length < 6 || nationalNumber?.length > 15) {
    setPhoneError(
      "يجب ادخال الصيغة الصحيحة لرقم الهاتف المحمول وفقا للدولة المختارة",
    );
    return false;
  }

  setPhoneError(null);
  return true;
};
